import { cn } from '@/lib/utils/helpers'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import {
  FINAL_LEDGER_LOCKED_INFO_MESSAGE,
  RESIDENT_STATUS_DATE_EDIT_ROLES,
} from '../../constants/residentConstants'
import { FileText, Info, ClipboardList } from 'lucide-react'
import { buildSkipAffidavitPortalUrl } from '@/features/reimbursements/utils/skipAffidavitPortalUrl'
import type {
  ResidentDefault,
  ResidentDefaultFormValues,
} from '../../types/resident'
import {
  getClaimDocumentLockState,
  isReasonFieldLocked,
  isFinalLedgerLockedFlag,
  isRentalProgressOptionDisabled,
  isRentalStatusOptionDisabled,
} from '../../utils/residentValidation'
import { ResidentDateField } from '../ResidentDateField'
import { RequiredAsterisk } from '../RequiredAsterisk'
import {
  deriveResidentDocumentStatusLabel,
  getResidentDocumentsForType,
  getResidentDocumentStatusTheme,
} from '../../utils/residentDocuments'
import {
  FIRST_MONTH_NOT_PAID_MONTH_VALUES,
  formatFirstMonthNotPaidMonthLabel,
} from '../../utils/residentPayload'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { ReimbursementStatusBar } from '@/features/reimbursements/components/detail/ReimbursementStatusBar'
import { ReimbursementFormStickyLayout } from '@/features/reimbursements/components/detail/ReimbursementFormStickyLayout'
import { buildReimbursementStatusBarProps } from '@/features/reimbursements/utils/buildReimbursementStatusBarProps'
import { PaymentLedgerFileField } from './PaymentLedgerFileField'
import { ResidentFileUploadField } from './ResidentFileUploadField'
import { ResidentUploadedDocumentsList } from './ResidentUploadedDocumentsList'
import type { ResidentDefaultFormProps, AuditNoteFieldName } from '../../types'
import { resolveReimbursementTypeCode } from '../../utils/residentHeaderStatus'
import {
  getReimbursementStatusTree,
  getResidentStatusTree,
} from '../../utils/residentListConstants'





const AUDIT_FIELDS: Record<string, AuditNoteFieldName> = {
  rental_status_date: { by: 'rental_status_date_edited_by', at: 'rental_status_date_edited_at' },
  adjusted_notice: { by: 'adjusted_notice_edited_by', at: 'adjusted_notice_edited_at' },
  movein_date: { by: 'movein_date_edited_by', at: 'movein_date_edited_at' },
  new_resident_lease_start_date: {
    by: 'new_resident_lease_start_date_edited_by',
    at: 'new_resident_lease_start_date_edited_at',
  },
  date_unit_is_back_on_market: {
    by: 'date_unit_is_back_on_market_edited_by',
    at: 'date_unit_is_back_on_market_edited_at',
  },
  adjust_max_movein_date: {
    by: 'adjust_max_movein_date_edited_by',
    at: 'adjust_max_movein_date_edited_at',
  },
}

function AuditNote({
  residentDefault,
  field,
}: {
  residentDefault?: ResidentDefault | null
  field: keyof typeof AUDIT_FIELDS
}) {
  if (!residentDefault) return null
  const map = AUDIT_FIELDS[field]
  if (!map?.by) return null
  const by = residentDefault[map.by] as string | undefined
  const at = residentDefault[map.at as string] as string | undefined
  if (!by) return null
  return (
    <p className="mt-1 text-xs italic text-slate-500">
      Edited By {by}
      {at ? ` - ${at}` : ''}
    </p>
  )
}

/**
 * Required Documentation card: status-colored top bar, title + pill badge header,
 * optional header extra (Final Ledger checkbox, prefill link), and the upload body.
 */
function DocumentUploadCard({
  label,
  status,
  extra,
  children,
}: {
  label: ReactNode
  status?: string
  extra?: ReactNode
  children: ReactNode
}) {
  const statusText = String(status ?? '').trim()
  const theme = getResidentDocumentStatusTheme(statusText)
  return (
    <div
      className={`flex min-w-0 flex-col gap-3 rounded-xl border border-slate-200 border-t-4 ${theme.topBar} bg-white p-4 shadow-sm`}
    >
      {/* Fixed header height keeps the dashed inputs aligned across cards regardless of the second line. */}
      <div className="flex min-h-[3rem] flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 text-sm font-semibold leading-snug text-slate-800">{label}</span>
          {statusText ? <span className={theme.badge}>{statusText}</span> : null}
        </div>
        {extra ? <div className="flex items-center">{extra}</div> : null}
      </div>
      {children}
    </div>
  )
}

/** Right-column field aligned with the document cards (e.g. Skip Affidavit dropdown). */
function DocumentUploadAdjacentField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex min-w-0 flex-col gap-2 rounded-xl border border-slate-200 border-t-4 border-t-slate-200 bg-white p-4 shadow-sm">
      <span className="text-sm font-semibold text-slate-800">{label}</span>
      {children}
    </div>
  )
}

function ResidentDefaultFormBodyLayout({
  children,
  statusBar,
}: {
  children: ReactNode
  statusBar: ReactNode | null
}) {
  if (statusBar) {
    return (
      <ReimbursementFormStickyLayout statusBar={statusBar}>{children}</ReimbursementFormStickyLayout>
    )
  }
  return <>{children}</>
}

// Angular `setFirstMonthDate`: years run 2015 through the current year only (no future years).
const FIRST_MONTH_YEAR_OPTIONS = (() => {
  const startYear = 2015
  const currentYear = new Date().getFullYear()
  return Array.from({ length: currentYear - startYear + 1 }, (_, i) => String(startYear + i))
})()

/** Fields still required in save payload when controls are not rendered (matches Angular full form state). */
const DEFAULT_TAB_HIDDEN_REGISTER_FIELDS: Array<keyof ResidentDefaultFormValues> = [
  'unit_number',
  'timely',
  'covid_19',
  'unused_security_deposit',
  'partial_payment_amount',
  'net_amount',
  'adjusted_reason',
  'loss_adjustment',
  'loss_adjusted_reason',
  'date_unit_is_back_on_market',
  'adjust_max_movein_date',
  'lease_month',
  'cdc_declaration',
  'payment_plan',
  'claim_expiration_date',
  'unit_vacancy',
  'claim_status',
  'jurisdiction_eviction_updates',
  'other_supporting_documents',
  'cdc_affidavit',
  'late_letter',
]

function formatRentalStatusLabel(status: string): string {
  return status
    .toLowerCase()
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ')
}

export function ResidentDefaultForm({
  control,
  register,
  setValue,
  values,
  errors,
  rentalList,
  statusList = null,
  missingFields,
  disabled,
  selectedTab,
  roleName,
  isCancelClaim = false,
  residentDefault,
  applicationData,
  isLibertyRole = false,
  showSectionTitle = true,
  onImmediateDocumentUpload,
  documentUploadingField = null,
  residentDocuments,
  onDownloadDocument,
  onRemoveDocument,
  canUploadDocuments = true,
  canRemoveDocuments = false,
  documentsProcessing = false,
  onRentalStatusDateUserChange,
  onRentalStatusUserChange,
  onFirstMonthChange,
}: ResidentDefaultFormProps) {
  const setBoolValue = (
    name: keyof ResidentDefaultFormValues,
    checked: boolean,
  ) => {
    setValue?.(name, checked ? '1' : '', { shouldDirty: true })
  }

  const residentStatusTree = getResidentStatusTree(statusList)
  const reimbursementStatusTree = getReimbursementStatusTree(statusList)
  const legacyStatusTree = rentalList?.rental_status ?? {}
  const statusTree =
    selectedTab === 'residentinfo'
      ? Object.keys(residentStatusTree).length > 0
        ? residentStatusTree
        : legacyStatusTree
      : Object.keys(reimbursementStatusTree).length > 0
        ? reimbursementStatusTree
        : legacyStatusTree

  const appRecord = applicationData as Record<string, unknown> | undefined
  const rdRecord = residentDefault as Record<string, unknown> | undefined

  useEffect(() => {
    if (!setValue) return
    if (selectedTab === 'residentinfo') {
      const status = String(appRecord?.resident_rental_status ?? rdRecord?.resident_rental_status ?? '').trim()
      const progress = String(appRecord?.resident_rental_progress ?? rdRecord?.resident_rental_progress ?? '').trim()
      if (status) setValue('rental_status', status, { shouldDirty: false })
      if (progress) setValue('rental_progress', progress, { shouldDirty: false })
      return
    }
    if (selectedTab === 'default') {
      const status = String(
        appRecord?.reimbursement_rental_status ?? rdRecord?.reimbursement_rental_status ?? '',
      ).trim()
      const progress = String(
        appRecord?.reimbursement_rental_progress ?? rdRecord?.reimbursement_rental_progress ?? '',
      ).trim()
      const claimType = String(rdRecord?.reimbursement_type ?? '').trim()
      if (status) setValue('rental_status', status, { shouldDirty: false })
      if (progress) setValue('rental_progress', progress, { shouldDirty: false })
      if (claimType) setValue('reimbursement_type', claimType, { shouldDirty: false })
    }
  }, [applicationData, residentDefault, selectedTab, setValue])

  const progressOptions = values.rental_status
    ? statusTree[values.rental_status]?.progress || {}
    : {}
  const reasonOptions = statusList?.reimbursement_reason ?? rentalList?.reason ?? {}
  const rentalStatusField = register('rental_status')

  const fieldClass = (name: keyof ResidentDefaultFormValues) =>
    `mt-1 h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none transition focus:border-button focus:ring-2 focus:ring-blue-100 ${
      errors[name] || missingFields.includes(name) ? 'border-red-500' : 'border-slate-200'
    }`

  const canEditStatusDate = (RESIDENT_STATUS_DATE_EDIT_ROLES as readonly string[]).includes(roleName)
  const reimbursementTypeCode = resolveReimbursementTypeCode(
    values.reimbursement_type,
    residentDefault?.reimbursement_type,
  )
  const isReimbursementStatus = reimbursementTypeCode !== null
  const showReason =
    values.rental_status && !['NOT_STARTED', 'ACTIVE', 'CLOSED'].includes(values.rental_status)
  // Angular keys Move-Out visibility off the PERSISTED record (resident_default), not live form
  // values: `residentEditForm.value.rental_status && resident_default.rental_status === 'CLOSED'
  //          && resident_default.rental_progress === 'EARLY_END'`.
  const showMoveOut =
    Boolean(values.rental_status) &&
    residentDefault?.rental_status === 'CLOSED' &&
    residentDefault?.rental_progress === 'EARLY_END'
  const reimbursementStatusName = values.rental_status
    ? statusTree[values.rental_status]?.name || formatRentalStatusLabel(values.rental_status)
    : ''
  const reasonLocked = isReasonFieldLocked({
    roleName,
    selectedTab,
    residentDefault,
    progressStatus: applicationData?.progress_status,
  })
  const documentLocks = getClaimDocumentLockState({
    canUploadFiles: canUploadDocuments,
    roleName,
    residentDefault,
    // Live checkbox — unchecking must unlock Payment Ledger upload immediately.
    isFinalLedger: values.is_final_ledger,
  })
  const documentUploadsLocked = documentLocks.uploadsLocked
  const documentRemovesLocked = documentLocks.removesLocked
  const paymentLedgerUploadsLocked = documentLocks.paymentLedgerUploadsLocked
  const paymentLedgerRemovesLocked = documentLocks.paymentLedgerRemovesLocked
  // Non-Liberty users see an info note (not the checkbox) when the Final Ledger is already locked.
  const showFinalLedgerLockedInfo =
    !isLibertyRole && isFinalLedgerLockedFlag(values.is_final_ledger || residentDefault?.is_final_ledger)
  // Angular `downloadClaimAffidavit`: opens `/api/skip_claim_affidavit/{application_id}?access_token=...`
  // in a new tab. The endpoint authenticates via the access_token query param, so it must be appended.
  const skipAffidavitReimbursementId =
    residentDefault?.fk_reimbursement_id ?? residentDefault?.reimbursement_id ?? null
  const prefilledSkipAffidavitUrl = applicationData?.application_id
    ? buildSkipAffidavitPortalUrl(applicationData.application_id, skipAffidavitReimbursementId)
    : null

  const firstMonthYearOptions = FIRST_MONTH_YEAR_OPTIONS.includes(values.first_month_not_paid_year)
    ? FIRST_MONTH_YEAR_OPTIONS
    : values.first_month_not_paid_year
    ? [...FIRST_MONTH_YEAR_OPTIONS, values.first_month_not_paid_year]
    : FIRST_MONTH_YEAR_OPTIONS

  const firstMonthOptions = (() => {
    const current = String(values.first_month_not_paid_month || '').trim()
    if (!current) return FIRST_MONTH_NOT_PAID_MONTH_VALUES
    if (FIRST_MONTH_NOT_PAID_MONTH_VALUES.includes(current)) return FIRST_MONTH_NOT_PAID_MONTH_VALUES
    const unpadded = String(Number(current))
    if (unpadded && FIRST_MONTH_NOT_PAID_MONTH_VALUES.includes(unpadded)) {
      return FIRST_MONTH_NOT_PAID_MONTH_VALUES
    }
    return [...FIRST_MONTH_NOT_PAID_MONTH_VALUES, current]
  })()

  const fieldLabelClass = 'mb-1 block text-sm font-medium text-slate-800'
  const sectionGridClass =
    'grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-5 xl:grid-cols-3 xl:items-start'

  const documentStatusLabel = (
    fileType: keyof ResidentDefaultFormValues,
    statusField: keyof ResidentDefaultFormValues,
  ) => {
    const fromForm = String(values[statusField] || '').trim()
    if (fromForm) return fromForm
    const derived = deriveResidentDocumentStatusLabel(
      getResidentDocumentsForType(residentDocuments, String(fileType)),
    )
    // Angular status inputs show MISSING when no file/status yet.
    return derived || 'MISSING'
  }

  const SectionWrapper = selectedTab === 'default' ? 'section' : 'div'
  const sectionWrapperClassName = selectedTab === 'default' ? 'border border-slate-200 rounded-xl bg-white p-5 shadow-sm mb-6' : ''

  const finalLedgerHeaderExtra = ['ROLE_ADMIN', 'ROLE_LIBERTY_DEFAULTS'].includes(roleName) ? (
    <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-slate-300"
        checked={isFinalLedgerLockedFlag(values.is_final_ledger)}
        onChange={(event) => setBoolValue('is_final_ledger', event.target.checked)}
        data-testid="resident-default-final-ledger-locked"
      />
      Final Ledger Locked
    </label>
  ) : showFinalLedgerLockedInfo ? (
    <span className="group relative inline-flex items-center">
      <Info className="h-4 w-4 shrink-0 text-red-500" aria-label="Final Ledger locked" />
      <span className="pointer-events-none absolute right-0 top-6 z-10 hidden w-64 rounded-md bg-slate-800 px-3 py-2 text-xs font-normal leading-snug text-white shadow-lg group-hover:block">
        {FINAL_LEDGER_LOCKED_INFO_MESSAGE}
      </span>
    </span>
  ) : null

  const skipAffidavitTitle = (
    <span className="block min-w-0">
      <span className="block">Skip Reimbursement Affidavit Form:</span>
      {prefilledSkipAffidavitUrl ? (
        canUploadDocuments ? (
          <a
            href={prefilledSkipAffidavitUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-0.5 block truncate text-sm font-semibold text-button underline hover:text-blue-700"
            data-testid="skip-claim-affidavit-prefilled-link"
          >
            Submit Pre-filled Skip Reimbursement Affidavit Form
          </a>
        ) : (
          <span
            className="mt-0.5 block truncate text-sm font-semibold text-slate-400 cursor-not-allowed"
            data-testid="skip-claim-affidavit-prefilled-disabled"
          >
            Submit Pre-filled Skip Reimbursement Affidavit Form
          </span>
        )
      ) : null}
    </span>
  )

  const skipAffidavitCard = (
    <DocumentUploadCard
      label={skipAffidavitTitle}
      status={documentStatusLabel('skip_claim_affidavit', 'skip_claim_affidavit_status')}
    >
      <input type="hidden" {...register('skip_claim_affidavit_status')} />
      <ResidentFileUploadField
        disabled={documentUploadsLocked}
        registerReturn={register('skip_claim_affidavit')}
        aria-label="Upload skip claim affidavit"
        dataTestId="resident-default-upload-skip-claim-affidavit"
        uploading={documentUploadingField === 'skip_claim_affidavit'}
        onImmediateUpload={
          onImmediateDocumentUpload
            ? (files) => onImmediateDocumentUpload('skip_claim_affidavit', files)
            : undefined
        }
      />
      <ResidentUploadedDocumentsList
        documents={residentDocuments}
        fileType="skip_claim_affidavit"
        testIdPrefix="resident-default-skip-claim-affidavit"
        canRemove={canRemoveDocuments && !documentRemovesLocked}
        processing={documentsProcessing}
        onDownload={(row) => onDownloadDocument?.('skip_claim_affidavit', row)}
        onRemove={onRemoveDocument}
      />
      <p className="text-xs text-slate-500">File will upload immediately upon selection.</p>
      {reimbursementTypeCode === 'SKIPPED' &&
      getResidentDocumentsForType(residentDocuments, 'eviction_lawsuit').length > 0 ? (
        <div
          className="mt-3 space-y-2 border-t border-slate-100 pt-3"
          data-testid="resident-default-eviction-file-list-in-skip-card"
        >
          <span className="block text-sm font-medium text-slate-700">
            Eviction File (with Dated Court Stamp)
          </span>
          <ResidentUploadedDocumentsList
            documents={residentDocuments}
            fileType="eviction_lawsuit"
            testIdPrefix="resident-default-unlawful-detainer-in-skip"
            canRemove={canRemoveDocuments && !documentRemovesLocked}
            processing={documentsProcessing}
            onDownload={(row) => onDownloadDocument?.('UDFILE', row)}
            onRemove={onRemoveDocument}
          />
        </div>
      ) : null}
    </DocumentUploadCard>
  )

  // One continuous 3-col grid: Skip Affidavit Form shows for SKIPPED, or for EVICTION when required === '1'.
  const showSkipAffidavitCard =
    reimbursementTypeCode === 'SKIPPED' ||
    (reimbursementTypeCode === 'EVICTION' && values.skip_claim_affidavit_required === '1')

  const statusBarProps =
    selectedTab === 'default'
      ? buildReimbursementStatusBarProps({ values, residentDocuments })
      : null

  return (
    <>
    <div
      data-testid={`resident-default-form-${selectedTab}`}
    >
      {showSectionTitle && selectedTab === 'residentinfo' ? (
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-black">
          Resident Information
        </h3>
      ) : null}
      {/* {selectedTab === 'default' &&
      shouldShowReimbursementNextStepsNotice(values.rental_status) &&
      onOpenEorp ? (
        <ReimbursementNextStepsNotice
          rentalList={rentalList}
          rentalStatus={values.rental_status}
          onOpenEorp={onOpenEorp}
        />
      ) : null} */}

      <ResidentDefaultFormBodyLayout
        statusBar={
          selectedTab === 'default' && statusBarProps ? (
            <ReimbursementStatusBar {...statusBarProps} />
          ) : null
        }
      >
          <SectionWrapper className={sectionWrapperClassName}>
        {selectedTab === 'default' ? (
          <div className="border-b border-slate-200 pb-4 mb-4">
            <SectionHeader
              icon={<ClipboardList className="h-4 w-4" />}
              title={`Required Information${reimbursementStatusName ? ` : ${reimbursementStatusName}` : ''}`}
              className="mb-0"
            />
          </div>
        ) : null}

        {/* Status row — always shown (Resident Info + Reimbursement) */}
        <div className={sectionGridClass} data-testid="resident-default-form-status-row">
          {selectedTab === 'residentinfo' ? (
            <label className={fieldLabelClass}>
              Unit Number
              <input
                className={fieldClass('unit_number')}
                readOnly={roleName === 'EXTERNAL_INSURANCE'}
                data-testid="resident-default-form-unit-number-input"
                {...register('unit_number')}
              />
            </label>
          ) : null}

          <label className={cn(fieldLabelClass, disabled && 'pointer-events-none opacity-70')}>
            Rental Status
          <select
            className={fieldClass('rental_status')}
            data-testid="resident-default-form-rental-status-select"
            {...rentalStatusField}
            onChange={(event) => {
              void rentalStatusField.onChange(event)
              onRentalStatusUserChange?.(event.target.value)
            }}
          >
            <option value="">Rental Status</option>
            {Object.entries(statusTree).map(([key, status]) => (
              <option key={key} value={key} disabled={isRentalStatusOptionDisabled(key, roleName, applicationData)}>
                {status.name || key}
              </option>
            ))}
          </select>
          {applicationData?.progress_status === 'Policy Activation Pending' ? (
            <p className="mt-1 text-xs font-medium text-slate-500">Policy must be activated before you can change this</p>
          ) : null}
        </label>

        <label className={cn(fieldLabelClass, disabled && 'pointer-events-none opacity-70')}>
          Progress
          {/* Controlled: options depend on rental_status, so the value must be re-applied once they render. */}
          <select
            className={fieldClass('rental_progress')}
            data-testid="resident-default-form-rental-progress-select"
            {...register('rental_progress')}
            value={values.rental_progress || ''}
          >
            <option value="" disabled>
              Select Progress
            </option>
            {Object.entries(progressOptions).map(([key, label]) => (
              <option
                key={key}
                value={key}
                disabled={isRentalProgressOptionDisabled(key, values.rental_status, roleName)}
              >
                {label}
              </option>
            ))}
          </select>
          {applicationData?.progress_status === 'Policy Activation Pending' ? (
            <p className="mt-1 text-xs font-medium text-slate-500">Policy must be activated before you can change this</p>
          ) : null}
        </label>

        {selectedTab === 'residentinfo' ? (
          <input type="hidden" {...register('reason_for_default')} />
        ) : null}
        {showReason && selectedTab !== 'residentinfo' ? (
          <label className={cn(fieldLabelClass, reasonLocked && 'pointer-events-none opacity-70')}>
            Reason
            {selectedTab === 'default' && isReimbursementStatus ? (
              <>
                {' '}
                <RequiredAsterisk />
              </>
            ) : null}
            <select
              className={fieldClass('reason_for_default')}
              data-testid="resident-default-form-reason-select"
              {...register('reason_for_default')}
            >
              <option value="">Select Reason</option>
              {Object.entries(reasonOptions).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {showMoveOut ? (
          <label className={cn(fieldLabelClass, disabled && 'pointer-events-none opacity-70')}>
            Move-Out Date
            <ResidentDateField
              control={control}
              name="moveout_date"
              error={!!(errors.moveout_date || missingFields.includes('moveout_date'))}
            />
          </label>
        ) : null}
      </div>

      {/* Reimbursement-only: matches Angular default-tab Required Information (not calculation / loss blocks). */}
      {selectedTab === 'default' && (isReimbursementStatus || isCancelClaim) ? (
        <>
          <hr className="my-6 border-slate-100" />

          {/* Row: request date | first month rent not paid | reimbursement start date */}
          <div
            className={sectionGridClass}
            data-testid="resident-default-form-reimbursement-row"
          >
            <label className={fieldLabelClass}>
              Reimbursement Request Date
              <ResidentDateField
                control={control}
                name="rental_status_date"
                disabled={disabled || !canEditStatusDate}
                error={!!(errors.rental_status_date || missingFields.includes('rental_status_date'))}
                onUserChange={onRentalStatusDateUserChange}
              />
              <AuditNote residentDefault={residentDefault} field="rental_status_date" />
            </label>

            <div data-testid="resident-default-form-first-month-not-paid">
              <span className={fieldLabelClass}>
                First Month Rent Not Paid <RequiredAsterisk />
              </span>
              <div className="grid grid-cols-2 gap-2">
                <select
                  className={`h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none transition focus:border-button focus:ring-2 focus:ring-blue-100 ${
                    errors.first_month_not_paid_month || missingFields.includes('first_month_not_paid_month')
                      ? 'border-red-500'
                      : 'border-slate-200'
                  }`}
                  data-testid="resident-default-form-first-month-not-paid-month"
                  {...register('first_month_not_paid_month')}
                  onChange={(e) => {
                    register('first_month_not_paid_month').onChange(e)
                    onFirstMonthChange?.()
                  }}
                >
                  <option value="">Select month</option>
                  {firstMonthOptions.map((month) => (
                    <option key={month} value={month}>
                      {formatFirstMonthNotPaidMonthLabel(month)}
                    </option>
                  ))}
                </select>
                <select
                  className={`h-10 w-full rounded-lg border bg-white px-3 text-sm outline-none transition focus:border-button focus:ring-2 focus:ring-blue-100 ${
                    errors.first_month_not_paid_year || missingFields.includes('first_month_not_paid_year')
                      ? 'border-red-500'
                      : 'border-slate-200'
                  }`}
                  data-testid="resident-default-form-first-month-not-paid-year"
                  {...register('first_month_not_paid_year')}
                  onChange={(e) => {
                    register('first_month_not_paid_year').onChange(e)
                    onFirstMonthChange?.()
                  }}
                >
                  {firstMonthYearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className={fieldLabelClass}>
              Reimbursement Start Date
              <ResidentDateField
                control={control}
                name="adjusted_notice"
                disabled={disabled || !canEditStatusDate}
                error={!!(errors.adjusted_notice || missingFields.includes('adjusted_notice'))}
              />
              <AuditNote residentDefault={residentDefault} field="adjusted_notice" />
            </label>
          </div>

          {/* Row: lease dates (Angular shows after date row for SKIPPED/EVICTION-style flow). */}
          {isLibertyRole ? (
            <div
              className={`mt-5 ${sectionGridClass}`}
              data-testid="resident-default-form-lease-row"
            >
              <label className={fieldLabelClass}>
                Lease Start Date
                <ResidentDateField
                  control={control}
                  name="lease_start_date"
                  error={!!(errors.lease_start_date || missingFields.includes('lease_start_date'))}
                />
              </label>
              <label className={fieldLabelClass}>
                Lease End Date
                <ResidentDateField
                  control={control}
                  name="lease_end_date"
                  error={!!(errors.lease_end_date || missingFields.includes('lease_end_date'))}
                />
              </label>
              <label className={fieldLabelClass}>
                Lease Date Verified
                <select
                  className={fieldClass('lease_verified')}
                  data-testid="resident-default-form-lease-verified-select"
                  {...register('lease_verified')}
                >
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              </label>
            </div>
          ) : null}
        </>
      ) : null}
      </SectionWrapper>

      {selectedTab === 'default' && (isReimbursementStatus || isCancelClaim) ? (
        <>
          {/* Document uploads — same document types as Angular rows (not extra COVID-era fields). */}
          <div
            className="mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:mt-8 sm:p-5"
            data-testid="resident-default-form-documents"
          >
            <div className="mb-4 flex items-center gap-2.5 border-b border-slate-200 pb-3">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#c7ddff] bg-[#dbeafe] text-[#2563eb]">
                <FileText className="h-4 w-4" aria-hidden="true" />
              </span>
              <h3 className="text-base font-bold uppercase tracking-tight text-slate-900">
                Required Documentation
              </h3>
            </div>

            {/*
              Single 3-col grid that wraps in order (Skipped works this way already):
              Lease | Ledger | Eviction → Skip Affidavit required? | Skip Affidavit Form
            */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 xl:items-start">
              <DocumentUploadCard
                label="Lease Agreement"
                status={documentStatusLabel('lease_agreement', 'lease_agreement_status')}
              >
                <input type="hidden" {...register('lease_agreement_status')} />
                <ResidentFileUploadField
                  disabled={documentUploadsLocked}
                  registerReturn={register('lease_agreement')}
                  aria-label="Upload lease agreement"
                  dataTestId="resident-default-upload-lease-agreement"
                  uploading={documentUploadingField === 'lease_agreement'}
                  onImmediateUpload={
                    onImmediateDocumentUpload
                      ? (files) => onImmediateDocumentUpload('lease_agreement', files)
                      : undefined
                  }
                />
                <ResidentUploadedDocumentsList
                  documents={residentDocuments}
                  fileType="lease_agreement"
                  testIdPrefix="resident-default-lease-agreement"
                  canRemove={canRemoveDocuments && !documentRemovesLocked}
                  processing={documentsProcessing}
                  onDownload={(row) => onDownloadDocument?.('lease_agreement', row)}
                  onRemove={onRemoveDocument}
                />
                <p className="text-xs text-slate-500">
                  File will upload immediately upon selection.
                </p>
              </DocumentUploadCard>

              <DocumentUploadCard
                label="Payment Ledger"
                status={documentStatusLabel('payment_ledger', 'payment_ledger_status')}
                extra={finalLedgerHeaderExtra}
              >
                <input type="hidden" {...register('payment_ledger_status')} />
                {!['ROLE_ADMIN', 'ROLE_LIBERTY_DEFAULTS'].includes(roleName) ? (
                  <input type="hidden" {...register('is_final_ledger')} />
                ) : null}
                <PaymentLedgerFileField
                  control={control}
                  disabled={paymentLedgerUploadsLocked}
                  skipPrompt={false}
                  registerReturn={register('payment_ledger')}
                  uploading={documentUploadingField === 'payment_ledger'}
                  onImmediateUpload={
                    onImmediateDocumentUpload
                      ? (files, isFinalLedger) =>
                          onImmediateDocumentUpload('payment_ledger', files, {
                            isFinalLedger,
                          })
                      : undefined
                  }
                />
                <ResidentUploadedDocumentsList
                  documents={residentDocuments}
                  fileType="payment_ledger"
                  testIdPrefix="resident-default-payment-ledger"
                  canRemove={canRemoveDocuments && !paymentLedgerRemovesLocked}
                  processing={documentsProcessing}
                  onDownload={(row) => onDownloadDocument?.('payment_ledger', row)}
                  onRemove={onRemoveDocument}
                />
                <p className="text-xs text-slate-500">
                  File will upload immediately upon selection.
                </p>
              </DocumentUploadCard>

              {reimbursementTypeCode === 'EVICTION' ? (
                <DocumentUploadCard
                  label="Eviction File (with Dated Court Stamp)"
                  status={documentStatusLabel('eviction_lawsuit', 'unlawful_detainer_status')}
                >
                  <input type="hidden" {...register('unlawful_detainer_status')} />
                  <ResidentFileUploadField
                    disabled={documentUploadsLocked}
                    multiple
                    registerReturn={register('eviction_lawsuit')}
                    aria-label="Upload eviction file"
                    dataTestId="resident-default-upload-eviction-lawsuit"
                    uploading={documentUploadingField === 'eviction_lawsuit'}
                    onImmediateUpload={
                      onImmediateDocumentUpload
                        ? (files) => onImmediateDocumentUpload('eviction_lawsuit', files)
                        : undefined
                    }
                  />
                  <ResidentUploadedDocumentsList
                    documents={residentDocuments}
                    fileType="eviction_lawsuit"
                    testIdPrefix="resident-default-unlawful-detainer"
                    canRemove={canRemoveDocuments && !documentRemovesLocked}
                    processing={documentsProcessing}
                    onDownload={(row) => onDownloadDocument?.('eviction_lawsuit', row)}
                    onRemove={onRemoveDocument}
                  />
                  <p className="text-xs text-slate-500">
                    File will upload immediately upon selection.
                  </p>
                  {/* Angular: when required === '0' but skip files exist, show download/remove list only. */}
                  {values.skip_claim_affidavit_required === '0' ? (
                    <div
                      className="mt-1 space-y-2"
                      data-testid="resident-default-skip-reimbursement-file-list"
                    >
                      <ResidentUploadedDocumentsList
                        documents={residentDocuments}
                        fileType="skip_claim_affidavit"
                        testIdPrefix="resident-default-skip-reimbursement-file"
                        canRemove={canRemoveDocuments && !documentRemovesLocked}
                        processing={documentsProcessing}
                        onDownload={(row) => onDownloadDocument?.('skip_claim_affidavit', row)}
                        onRemove={onRemoveDocument}
                      />
                    </div>
                  ) : null}
                </DocumentUploadCard>
              ) : null}

              {reimbursementTypeCode === 'EVICTION' && isLibertyRole ? (
                <DocumentUploadAdjacentField label="Skip Affidavit required?">
                  <select
                    className={fieldClass('skip_claim_affidavit_required').replace('mt-1 ', '')}
                    data-testid="resident-default-skip-affidavit-required"
                    {...register('skip_claim_affidavit_required')}
                  >
                    <option value="1">Yes</option>
                    <option value="0">No</option>
                  </select>
                </DocumentUploadAdjacentField>
              ) : null}

              {showSkipAffidavitCard ? skipAffidavitCard : null}
            </div>
          </div>

          {/* Move-out / new resident row (Angular: after uploads). */}
          <div
            className={`mt-6 sm:mt-8 ${sectionGridClass}`}
            data-testid="resident-default-form-move-out-row"
          >
            <label className={fieldLabelClass}>
              Move-Out Date
              <ResidentDateField
                control={control}
                name="moveout_date"
                error={!!(errors.moveout_date || missingFields.includes('moveout_date'))}
              />
            </label>
            <label className={fieldLabelClass}>
              New Resident Lease Start Date
              <ResidentDateField
                control={control}
                name="new_resident_lease_start_date"
                error={
                  !!(
                    errors.new_resident_lease_start_date ||
                    missingFields.includes('new_resident_lease_start_date')
                  )
                }
              />
              <AuditNote residentDefault={residentDefault} field="new_resident_lease_start_date" />
            </label>
            <label className={fieldLabelClass}>
              New Resident Move-In Date
              <ResidentDateField
                control={control}
                name="movein_date"
                error={!!(errors.movein_date || missingFields.includes('movein_date'))}
              />
              <AuditNote residentDefault={residentDefault} field="movein_date" />
            </label>
          </div>

        </>
      ) : null}

      {/* Covid / payload: keep `notes` registered once on default tab (Angular hides dedicated notes UI here). */}
      {selectedTab === 'default' ? (
        <textarea {...register('notes')} className="sr-only h-0 w-0 resize-none overflow-hidden opacity-0" tabIndex={-1} aria-hidden />
      ) : null}

      {/* Preserve full save payload when controls are not on screen (all default-tab rental statuses). */}
      {selectedTab === 'default' ? (
        <div className="sr-only" aria-hidden>
          {DEFAULT_TAB_HIDDEN_REGISTER_FIELDS.map((name) => (
            <input key={name} type="hidden" {...register(name)} />
          ))}
        </div>
      ) : null}

      {/* `message` lives on `ResidentPublicNotesMessagesCard` unless external insurance (no public card). */}
      {selectedTab === 'default' && roleName === 'EXTERNAL_INSURANCE' ? (
        <input type="hidden" {...register('message')} />
      ) : null}
      </ResidentDefaultFormBodyLayout>
    </div>
    </>
  )
}
